import type {
  SemanticFormatSpec,
  SemanticMaterialitySpec,
} from "@/features/data-hub/model/semanticFormat";
import {
  buildFormatFromMeasurementValues,
  buildMaterialityFromMeasurementValues,
  createEmptyMeasurementFormValues,
  measurementToFormValues,
  type SemanticMeasurementFormValues,
} from "@/features/data-hub/model/semanticMeasurement";

export type SemanticMetric = {
  key?: string;
  name?: string;
  description?: string;
  combination_strategy?: string;
  component_measures?: string[];
  metric_type?: string;
  grain?: string[];
  executable?: boolean;
  non_executable_reason?: string;
  confidence?: number;
  provenance?: string;
  format?: SemanticFormatSpec;
  materiality?: SemanticMaterialitySpec;
};

export type SemanticMetricFormValues = {
  name: string;
  description: string;
  combinationStrategy: string;
  componentMeasures: string[];
  metricType: string;
  /** Dimension keys this metric can be sliced by. */
  grain: string[];
  measurement: SemanticMeasurementFormValues;
};

export const METRIC_COMBINATION_STRATEGY_OPTIONS = [
  "passthrough",
  "ratio",
  "difference",
  "sum",
  "custom",
] as const;

export function slugifyMetricKey(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^\w]+/g, "_")
    .replace(/^_|_$/g, "");
}

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object"
    ? (value as Record<string, unknown>)
    : {};
}

export function metricToFormValues(metric: SemanticMetric): SemanticMetricFormValues {
  return {
    name: (metric.name ?? "").trim(),
    description: (metric.description ?? "").trim(),
    combinationStrategy: (metric.combination_strategy ?? "ratio").trim() || "ratio",
    componentMeasures: Array.isArray(metric.component_measures)
      ? [...metric.component_measures]
      : [],
    metricType: (metric.metric_type ?? "").trim(),
    grain: Array.isArray(metric.grain) ? [...metric.grain] : [],
    measurement: measurementToFormValues(metric.format, metric.materiality),
  };
}

export function createEmptyMetricFormValues(): SemanticMetricFormValues {
  return {
    name: "",
    description: "",
    combinationStrategy: "ratio",
    componentMeasures: [],
    metricType: "",
    grain: [],
    measurement: createEmptyMeasurementFormValues(),
  };
}

export function buildMetricFromFormValues(
  values: SemanticMetricFormValues,
  existing?: SemanticMetric,
): SemanticMetric {
  const trimmedName = values.name.trim();
  const key = (existing?.key ?? "").trim() || slugifyMetricKey(trimmedName);

  return {
    ...existing,
    key,
    name: trimmedName,
    description: values.description.trim() || undefined,
    combination_strategy: values.combinationStrategy.trim() || "ratio",
    component_measures: values.componentMeasures.filter((measureKey) =>
      measureKey.trim(),
    ),
    metric_type: values.metricType.trim() || undefined,
    grain: values.grain.filter((dimensionKey) => dimensionKey.trim()),
    // Explicit rather than relying on the `...existing` spread: the form owns both
    // specs now, so an edit that clears one must actually clear it.
    format: buildFormatFromMeasurementValues(values.measurement),
    materiality: buildMaterialityFromMeasurementValues(values.measurement),
    confidence: 1,
    provenance: "interview",
  };
}

export function selectSemanticMetricsFromGeneratedModel(
  generatedModel: Record<string, unknown>,
): SemanticMetric[] {
  const document = asRecord(generatedModel.document);
  const fromDocument = document.metrics;
  if (Array.isArray(fromDocument)) {
    return fromDocument as SemanticMetric[];
  }
  return [];
}

export function isSemanticMetricKeyTaken(
  metrics: SemanticMetric[],
  key: string,
  excludeIndex: number | null = null,
): boolean {
  const normalized = key.trim().toLowerCase();
  if (!normalized) {
    return false;
  }
  return metrics.some((metric, index) => {
    if (excludeIndex !== null && index === excludeIndex) {
      return false;
    }
    return (metric.key ?? "").trim().toLowerCase() === normalized;
  });
}
